import React, { useState, useEffect, useRef } from 'react'
import { Form, Formik } from 'formik'
import * as yup from 'yup'
import { agentVerifyEmail } from 'api/agents'
import { SyncData } from 'api/users'
import { screenEmail, triggerLoginEmailTocken } from 'api/auth'
import { useAccountCreation } from 'contexts/AccountCreation'
import { useLocalForage } from 'contexts/LocalForage'
import Fieldset from '../FormComponents/Fieldset'
import FieldsetItem from '../FormComponents/Fieldset/FieldsetItem'
import Input from '../FormComponents/Input/Input'
import ButtonCta from '../Button/ButtonCta'
import Section from '../Section'
import Wrapper from '../Wrapper'
import styled from 'styled-components'
import FieldMessage from '../FormComponents/FieldMessage'
import { testPassword, emailRegex } from 'utils/validators'
import { AGENT_CHANNEL, NO_INTERNET_OFFLINE_MODE, ALLOWED_PROVINCES } from 'utils/constants'
import { successColor } from 'styles/colors'
import Icon from 'components/Icon'
import PasswordInput from '../FormComponents/Input/PasswordInput'
import toast from 'react-hot-toast'
import { patronScanPolicy } from './CreationFormSecondStep/consents'
import { IS_PATRON_SCAN_FEATURE_ENABLED } from 'utils/constants'
import omit from 'lodash/omit'

const CustomValidatorCheckMark = styled(Icon)`
  position: absolute;
  top: 55px;
  right: 24px;
  transform: translateY(-50%);
`
const ConsentContainer = styled.div`
  margin: 1rem 0;
`
const CreationFormFirstStep = () => {
  const { user, setUser, resetUser, nextStep, setFlow, startTimer } = useAccountCreation()
  const { getStoredItem } = useLocalForage()
  const [accountMessage, setAccountMessage] = useState({
    type: null,
    message: null,
  })
  const [accountMessageEmail, setAccountMessageEmail] = useState({
    type: null,
    message: null,
    icon: null,
  })
  const [showPasswordField, setShowPasswordField] = useState(false)
  const [buttonText, setButtonText] = useState('Create Account')
  const [accountExist, setAccountExist] = useState(false)
  const [returningUserInfo, setReturningUserInfo] = useState({})
  const [emailChecking, setEmailChecking] = useState(false)
  const [disableReturningConsumerFlowButton, setDisableReturningConsumerFlowButton] = useState(false)
  const inputRef = useRef()

  const [isSendEmailButtonDisabled, setIsSendEmailButtonDisabled] = useState(false)
  const [isDisplaySendEmailButton, setIsDisplaySendEmailButton] = useState(false)
  const [emailIdOfExistingUser, setEmailIdOfExistingUser] = useState()
  const [agentInformation, setAgentInformation] = useState()
  const submitButtonRef = useRef()
  const debounce = (func, delay) => {
    let timeout
    return (...args) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => func(...args), delay)
    }
  }

  const checkEmail = async (event) => {
    const email = event.target.value

    resetDefaultState()

    if (emailRegex.test(email)) {
      setEmailChecking(true)

      if (navigator.onLine) {
        try {
          const response = await agentVerifyEmail({ email })
          if (response.emailExists === true) {
            if (response.isUserBeforeDesiredDate === false) {
              setAccountMessage({
                type: 'error',
                message: 'You already have an account. Ask your brand ambassador for more details',
                icon: 'cross-red',
              })
              setDisableReturningConsumerFlowButton(true)
            } else {
              setAccountMessage({
                type: 'success',
                message: response.firstName ? `Welcome back ${response.firstName}` : 'Welcome back',
              })
            }
            setAccountExist(true)
            setShowPasswordField(false)
            setReturningUserInfo(response)
            setButtonText('Log in')

            if (agentInformation?.channel) {
              if (
                agentInformation?.channel.replace(' ', '').toLowerCase() ===
                  AGENT_CHANNEL.ownedRetail.replace(' ', '').toLowerCase() &&
                ALLOWED_PROVINCES.includes(response?.province)
              ) {
                setIsDisplaySendEmailButton(true)
                setEmailIdOfExistingUser(email)
              }
            }
          } else {
            const response = await screenEmail(email)

            // Error
            if (response.error === 'Email account invalid') {
              setDisableReturningConsumerFlowButton(true)
              setAccountMessageEmail({
                type: 'error',
                message: 'Please use a valid email address',
                icon: 'cross-red',
              })
            } else if (
              response.status === 'unknown' ||
              response.error === 'Email domain invalid' ||
              response.error === 'Email address invalid'
            ) {
              setDisableReturningConsumerFlowButton(true)
              if (response.error === 'Email address invalid') {
                setAccountMessageEmail({
                  type: 'error',
                  message: 'Email address is not valid please use valid email address',
                  icon: 'cross-red',
                })
              } else {
                setAccountMessageEmail({
                  type: 'error',
                  message: 'Email domain is not valid please use valid email address',
                  icon: 'cross-red',
                })
              }
            } else {
              // Success
              setEmailChecking(false)
              setDisableReturningConsumerFlowButton(false)

              if (submitButtonRef.current) {
                submitButtonRef.current.focus()
              }
            }
          }
        } catch (error) {
          if (!navigator.onLine) {
            setAccountMessage({
              type: 'success',
              message: NO_INTERNET_OFFLINE_MODE,
              icon: 'checkmark-green',
            })
          } else {
            setDisableReturningConsumerFlowButton(true)
            setAccountMessageEmail({
              type: 'error',
              message: 'An error has occurred during agent validation, please ask an agent to login again',
              icon: 'cross-red',
            })
          }
          setEmailChecking(false)
        }
      } else {
        setAccountMessage({
          type: 'success',
          message: NO_INTERNET_OFFLINE_MODE,
          icon: 'checkmark-green',
        })
      }
      setEmailChecking(false) // Reset emailChecking to false after the check is complete
    }
  }

  useEffect(() => {
    const firstTime = () => {
      if (navigator.onLine) {
        toast.success('online')
        SyncData()
      } else {
        toast.success('Offline, will sync when online')
      }
    }
    firstTime()
  }, [])

  useEffect(() => {
    const fetchAgentInfo = async () => {
      const agentInformation = await getStoredItem('agentInformation')
      setAgentInformation(agentInformation)
    }

    fetchAgentInfo()
  }, [])
  useEffect(() => {
    if (inputRef && inputRef.current) {
      const debouncedCheckEmail = debounce(checkEmail, 500)
      inputRef.current.addEventListener('input', debouncedCheckEmail)

      // Call checkEmail on mount if emailValue is not empty and valid
      if (user.email && emailRegex.test(user.email)) {
        checkEmail({ target: { value: user.email } })
      }
    }
  }, [agentInformation])

  useEffect(() => {
    function checkOnlineStatus() {
      if (navigator.onLine) {
        toast.success('online')
        SyncData()
      } else {
        toast.success('Offline, will sync when online')
      }
    }
    // Listen for online event
    window.addEventListener('online', checkOnlineStatus)
    window.addEventListener('offline', checkOnlineStatus)

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('online', checkOnlineStatus)
      window.removeEventListener('offline', checkOnlineStatus)
    }
  }, [navigator.onLine])

  const resetDefaultState = () => {
    setAccountExist(false)
    resetUser()
    setReturningUserInfo({})
    setShowPasswordField(true)
    setButtonText('Create Account')
    setAccountMessage({
      type: 'error',
      message: '',
      icon: null,
    })
    setDisableReturningConsumerFlowButton(false)
    setAccountMessageEmail({ type: '', message: '', icon: null })
    setIsDisplaySendEmailButton(false)
    setIsSendEmailButtonDisabled(false)
  }

  const handleFirstStepSubmit = async (values) => {
    const agentSource = await getStoredItem('agentSource')
    const agentErpSource = await getStoredItem('selectedAgentErpSource')

    if (accountExist) {
      setFlow('ReturningConsumer')
      setUser({
        email: values.email,
        ...omit(returningUserInfo, ['phoneNumber']),
      })
      nextStep()
    } else {
      setFlow('AccountCreation')
      const userValue = {
        ...user,
        email: values.email,
        password: values.password,
        agentSource,
        utmSource: agentErpSource,
      }
      if (!agentErpSource?.length > 0) delete userValue.utmSource

      setUser(userValue)
      nextStep()
    }
  }

  const getValidationSchema = () => {
    let schema = yup.object().shape({
      email: yup
        .string()
        .email('Please use a valid email address')
        .required('Email is required')
        .matches(emailRegex, 'Please use a valid email address'),
    })

    if (showPasswordField) {
      schema = schema.concat(
        yup.object().shape({
          password: yup
            .string()
            .required('Password is required')
            .test({
              firstName: 'password-validator',
              test(value) {
                return testPassword(value, this)
              },
            }),
        }),
      )
    }

    return schema
  }
  const triggerLoginLink = async () => {
    setIsSendEmailButtonDisabled(true)
    const payload = {
      email: emailIdOfExistingUser,
      source: 'agent',
    }
    try {
      const response = await triggerLoginEmailTocken(payload)

      if (response) {
        toast.success('Email has been sent')
      }
    } catch (error) {
      console.log(error)
      toast.error('Error occurred while sending Email.')
    }
  }

  return (
    <Section title={'Enter your email to start'}>
      <Wrapper>
        <Formik
          initialValues={{ email: user.email || '', password: user.password || '' }}
          validationSchema={getValidationSchema()}
          onSubmit={handleFirstStepSubmit}
          validateOnChange
        >
          {({ errors, touched, values, handleChange, handleBlur, isSubmitting }) => {
            const modifiedHandleChange = (event) => {
              // Start timer to track account creation completion time
              if (event.target.value.length === 5) startTimer()
              handleChange(event)
            }

            return (
              <Form>
                <Fieldset>
                  <FieldsetItem xs="1-1">
                    <div style={{ position: 'relative' }}>
                      <Input
                        type="email"
                        label="Email address"
                        name="email"
                        placeholder="johndoe@domain.com"
                        onChange={modifiedHandleChange}
                        onBlur={handleBlur}
                        value={values.email}
                        autoComplete="off"
                        meta={{
                          error: errors.email,
                          touched: touched.email,
                        }}
                        ref={inputRef}
                        style={{
                          borderBottom: accountExist ? `1px solid ${successColor}` : '1px solid black',
                        }}
                      />
                      {!errors.email && values.email.length > 1 && (
                        <CustomValidatorCheckMark icon={'checkmark-green'} small />
                      )}
                    </div>
                  </FieldsetItem>

                  {accountMessageEmail.message && accountMessageEmail.message.length > 0 && (
                    <FieldsetItem xs="1-1" noPaddingTop>
                      <FieldMessage type={accountMessageEmail.type} icon={accountMessageEmail.icon}>
                        {accountMessageEmail.message}
                      </FieldMessage>
                    </FieldsetItem>
                  )}

                  {showPasswordField && (
                    <FieldsetItem xs="1-1">
                      <PasswordInput
                        value={values.password}
                        meta={{
                          error: errors.password,
                          touched: touched.password,
                        }}
                        label="Create new password"
                        name="password"
                        placeholder="****"
                        onChange={modifiedHandleChange}
                        onBlur={handleBlur}
                        autoComplete="off"
                      />
                    </FieldsetItem>
                  )}

                  {accountMessage.message && accountMessage.message.length > 0 && (
                    <FieldsetItem xs="1-1" noPaddingTop>
                      <FieldMessage type={accountMessage.type} icon={false}>
                        {accountMessage.message}
                      </FieldMessage>
                    </FieldsetItem>
                  )}
                  <FieldsetItem xs="1-1">
                    <ButtonCta
                      type="submit"
                      disabled={disableReturningConsumerFlowButton || !!errors.email || !!errors.password}
                      isLoading={emailChecking}
                      ref={submitButtonRef}
                    >
                      {buttonText}
                    </ButtonCta>
                  </FieldsetItem>
                  <FieldsetItem xs="1-1">
                    {isDisplaySendEmailButton && (
                      <ButtonCta
                        onClick={() => triggerLoginLink()}
                        disabled={isSendEmailButtonDisabled}
                        isLoading={emailChecking}
                      >
                        {`Send Login Link`}
                      </ButtonCta>
                    )}
                  </FieldsetItem>
                </Fieldset>
              </Form>
            )
          }}
        </Formik>
        {IS_PATRON_SCAN_FEATURE_ENABLED && <ConsentContainer>{patronScanPolicy}</ConsentContainer>}
      </Wrapper>
    </Section>
  )
}

export default CreationFormFirstStep
